// @ts-check

import { LitElement, css, html } from 'lit';
import { customElement} from 'lit/decorators.js';
import { toContext } from 'ol/render';
import { Feature } from 'ol';
import { Style, Icon, Stroke } from 'ol/style';
import { Point, LineString } from 'ol/geom';
import { LayersFactory } from '@maersk-global/map-layers';

import { VoyageRoutingModel,  } from '../../../src/models/voyage-routing-model'
import { mpsToKnots } from '../../../src/utils/helpers'

const KNOTS_PER_MS = 1.94384;

// We render the weather indicators similarly to SPOS: the indicators originate at a fixed radius around the ship, and extend further inward at higher magnitudes.
// The indicators are set at different radii from the ship so they interfere with each other as little as possible. Since the wave (sea) and wind indicators
// always come from the same direction, they are connected, while the swell and current have a small gap between them.

const SWELL_SCALING = {
    multiplier: 1,
    maxValue: 10,
    styleSizeFactor: 10, // see SwellStyles in MG map-layers package
    innerRadius: 0,
    outerRadius: 45
};

const CURRENT_SCALING = {
    multiplier: 2,
    maxValue: 3,
    styleSizeFactor: 30, // see CurrentsStyles in MG map-layers package
    innerRadius: 48,
    outerRadius: 65
};

const WAVE_SCALING = {
    multiplier: 1,
    maxValue: 10,
    styleSizeFactor: 10, // see WaveStyles in MG map-layers package
    innerRadius: 0,
    outerRadius: 25
};

const WIND_SCALING = {
    innerRadius: 25
};

const dialBackground = html`
    <svg width="184" height="197" viewBox="0 0 184 197" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="95" cy="100" r="75" fill="white" stroke="#003D6D" stroke-width="2"/>
        <path d="M97.16 7.8V16.6L91.816 7.8H90.44V19H91.784V10.136L97.224 19H98.52V7.8H97.16Z" fill="white"/>
        <path d="M94.224 192.16C96.848 192.16 98.048 191.024 98.048 189.152C98.048 187.472 97.376 186.672 95.584 185.968L94.048 185.36C92.832 184.896 92.304 184.528 92.304 183.44C92.304 182.4 92.912 181.888 94.528 181.888C96.064 181.888 97.184 182.352 97.6 182.576L97.456 181.2C97.104 181.024 96.256 180.64 94.528 180.64C91.84 180.64 90.896 181.888 90.896 183.44C90.896 184.928 91.456 185.792 93.136 186.448L94.944 187.152C96.192 187.632 96.656 188.032 96.656 189.12C96.656 190.272 96.064 190.912 94.224 190.912C92.88 190.912 91.568 190.336 90.816 189.904V191.376C91.312 191.648 92.56 192.16 94.224 192.16Z" fill="white"/>
        <path d="M177.816 104.8V100.88H182.216V99.664H177.816V96H183.112V94.8H176.44V106H183.224V104.8H177.816Z" fill="white"/>
        <path d="M3.104 106H4.752L7.344 96.016L9.92 106H11.584L14.192 94.8H12.784L10.72 104.416L8.304 94.8H6.368L3.952 104.416L1.888 94.8H0.48L3.104 106Z" fill="white"/>
    </svg>
`;

const dialMarks = html`
    <svg width="184" height="197" viewBox="0 0 184 197" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="95" cy="100" r="75" stroke="#003D6D" stroke-width="2"/>
        <line x1="95" y1="26" x2="95" y2="34" stroke="#003D6D" stroke-width="2"/>
        <line x1="21" y1="100" x2="29" y2="100" stroke="#003D6D" stroke-width="2"/>
        <line x1="161" y1="100" x2="169" y2="100" stroke="#003D6D" stroke-width="2"/>
        <line x1="95" y1="166" x2="95" y2="174" stroke="#003D6D" stroke-width="2"/>
        <line x1="42.7071" y1="47.2929" x2="48.364" y2="52.9497" stroke="#003D6D" stroke-width="2"/>
        <line x1="147.364" y1="47.7071" x2="141.707" y2="53.364" stroke="#003D6D" stroke-width="2"/>
        <line y1="-1" x2="8" y2="-1" transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 42 151.657)" stroke="#003D6D" stroke-width="2"/>
        <line y1="-1" x2="8" y2="-1" transform="matrix(-0.707107 -0.707107 -0.707107 0.707107 147.657 153.657)" stroke="#003D6D" stroke-width="2"/>
    </svg>
`

const dialCanvasDimensions = [150, 150];
const dialCanvasRadius = 75;
const dialCanvasCenter = [75, 75];

const shipShape = escape(`
    <svg width="45" height="21" viewBox="0 0 45 21" fill="none" xmlns="http://www.w3.org/2000/svg">
        <mask id="path-1-inside-1" fill="white">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M30 20.7808L45 10.7808L30 0.780761L0 0.78076L-8.74228e-07 20.7808L30 20.7808Z"/>
        </mask>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M30 20.7808L45 10.7808L30 0.780761L0 0.78076L-8.74228e-07 20.7808L30 20.7808Z" fill="#003D6D"/>
        <path d="M45 10.7808L45.5547 11.6128L46.8028 10.7808L45.5547 9.94871L45 10.7808ZM30 20.7808L30 21.7808L30.3028 21.7808L30.5547 21.6128L30 20.7808ZM30 0.780761L30.5547 -0.0512892L30.3028 -0.219239L30 -0.219239L30 0.780761ZM0 0.78076L4.37114e-08 -0.21924L-1 -0.21924L-1 0.78076L0 0.78076ZM-8.74228e-07 20.7808L-1 20.7808L-1 21.7808L-9.17939e-07 21.7808L-8.74228e-07 20.7808ZM44.4453 9.94871L29.4453 19.9487L30.5547 21.6128L45.5547 11.6128L44.4453 9.94871ZM29.4453 1.61281L44.4453 11.6128L45.5547 9.94871L30.5547 -0.0512892L29.4453 1.61281ZM-4.37114e-08 1.78076L30 1.78076L30 -0.219239L4.37114e-08 -0.21924L-4.37114e-08 1.78076ZM0.999999 20.7808L1 0.78076L-1 0.78076L-1 20.7808L0.999999 20.7808ZM30 19.7808L-8.30516e-07 19.7808L-9.17939e-07 21.7808L30 21.7808L30 19.7808Z" fill="white" mask="url(#path-1-inside-1)"/>
    </svg>
`);

const shipShapeDimensions = [45, 21];

const routeIcon = html`
    <svg width="52" height="12" viewBox="0 0 52 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="41" y1="6" x2="11" y2="6" stroke="currentColor" stroke-width="3"/>
        <circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="3"/>
        <circle cx="46" cy="6" r="4.5" stroke="currentColor" stroke-width="3"/>
    </svg>
`;

// Plots the ship on a compass with various weather indicators.
// Weather elements should be passed as an array, either as they come from the spot weather API or from waypoints on a
// ShortestWeatherOptimized or WeatherOptimized route calculation.
export class VoyageShipWeatherExpectation extends LitElement {
    constructor() {
        super();

        this.heading = 0;

        this._weatherElements = {
            wave: { value: 0, heading: 0 },
            wind: { value: 0, heading: 0 },
            swell: { value: 0, heading: 0 },
            current: { value: 0, heading: 0 }
        };

        this.stylesFactory = new LayersFactory({
            chartWMSUrl: '',
            weatherWMSUrl: '',
            weatherBorderWMSUrl: '',
            weatherNonGridUrl: '',
            player: null
        }).styles;

        this.routeName = '';
        this.routeColor = '';

        this.fetchLimits = this.fetchLimits.bind(this);
    }

    static get properties() {
        return {
            heading: { type: Number },
            weatherElements: { type: Object },
            routeName: { type: String },
            routeColor: { type: String }
        };
    }

    _parseElement(element, target, conversionFactor, roundToDecimals) {
        const roundingFactor = Math.pow(10, roundToDecimals);
        target.value = Math.round(element[element.length - 1] * conversionFactor * roundingFactor + Number.EPSILON) / roundingFactor;
        target.heading = element[element.length - 2];
    }

    set weatherElements(value) {
        const oldValue = this.weatherElements;

        const newWeatherElements = {
            wave: { value: 0, heading: 0 },
            wind: { value: 0, heading: 0 },
            swell: { value: 0, heading: 0 },
            current: { value: 0, heading: 0 }
        };
        for (let element of value) {
            if (element.element === "Wind10M" || element.element === 2) {
                this._parseElement(element.value, newWeatherElements.wind, 1, 0);
            } else if (element.element === "Wave" || element.element === 8) {
                this._parseElement(element.value, newWeatherElements.wave, 1, 1);
            } else if (element.element === "Swell" || element.element === 10) {
                this._parseElement(element.value, newWeatherElements.swell, 1, 1);
            } else if (element.element === "Currents" || element.element === 21) {
                this._parseElement(element.value, newWeatherElements.current, KNOTS_PER_MS, 1);
            }
        }

        this._weatherElements = newWeatherElements;
        this.requestUpdate('weatherElements', oldValue);
    }

    get weatherElements() {
        return this._weatherElements;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                align-items: stretch;
                background: rgba(0, 0, 0, 0.5)
            }
            
            .wind-container{
                display: flex;
                flex-direction: row;
                gap: 30px;
            }

            .dial {
                position: relative;
                height: 197px;
                width: 192px;
            }

            .dial svg {
                position: absolute;
                top: 0;
                bottom: 0;
                left: 4px;
                right: 4px;
            }

            .dial canvas {
                position: absolute;
                left: 24px;
                top: 25px;
                width: 150px;
                height: 150px;
            }

            .values {
                display: flex;
                flex-direction: column;
                justify-content: space-evenly;

                padding: 0 10px;

                font-family: var(--component-paragraph-ff, 'serif');
                font-size: 14px;
                line-height: 16px;
                color: var(--neutrals-grey-10, white);
            }

            .value {
                font-weight: bold;
            }

            .header-container {
                position: relative;
                width: 36px;
                height: 197px;
                background-color: var(--neutrals-grey-10, white);
            }

            .header {
                position: absolute;
                height: 36px;
                width: 197px;
                bottom: -36px;
                left: 0;
                transform: rotate(-90deg);
                transform-origin: top left;

                display: flex;
                justify-content: space-evenly;
                align-items: center;
            }

            .route-name {
                color: var(--neutrals-grey-02, black);
                font-family: var(--component-paragraph-ff, 'serif');
                font-size: 12px;
                line-height: 16px;
                text-transform: uppercase;
            }

            .avoidActual {
                color: var(--voyage-error-red, red);
            }

            .warningActual {
                color: var(--warning-yellow-7, yellow);
            }
        `;
    }

    render() {
        return html`
            ${this.routeName && this.routeColor ? html`
                <div class="header-container" style="color: ${this.routeColor}">
                    <div class="header">
                        ${routeIcon}
                        <div class="route-name">${this.routeName}</div>
                    </div>
                </div>
            ` : html``}
            <div class="dial">
                ${dialBackground}
                <canvas></canvas>
                ${dialMarks}
            </div>
            <div class="values">
                <div class="value-container">
                    <div class="heading">sea</div>
                    <div class="value">${this.weatherElements.wave.value} m</div>
                </div>
                <div class="value-container">
                    <div class="heading">swell</div>
                    <div class="value">${this.weatherElements.swell.value} m</div>
                </div>
                <div class="value-container">
                    <div class="heading">current</div>
                    <div class="value">${this.weatherElements.current.value} kt</div>
                </div>
                <div class="value-container">
                    <div class="heading">wind</div>
                    <div class="wind-container">
                        <div class=${this.classWarnAvoid(this.weatherElements.wind.value, 'Wind10M')}>${this.weatherElements.wind.value} m/s</div>
                        <div class=${this.classWarnAvoid(this.weatherElements.wind.value, 'Wind10M')}>${mpsToKnots(this.weatherElements.wind.value).toFixed(1)} kt</div>
                    </div>
                </div>
            </div>
        `;
    }

    classWarnAvoid(value, key) {
        let result = 'value';

        if (this._weatherLimits && this._weatherLimits[key]) {
            const limit = this._weatherLimits[key];
            if (limit.avoid !== null && limit.avoid < value) {
                result += ' avoidActual';
            } else if (limit.warn !== null && limit.warn < value) {
                result += ' warningActual';
            }
        }

        return result;
    }

    updated(changedProperties) {
        // Only redraw the layers if something has changed
        if (changedProperties.size) {
            this.drawLayers();
        }
    }

    firstUpdated() {
        // When using immediate rendering in OpenLayers, it cannot plot icon images using a src. So we have to
        // "load" the image ourselves first, then use that image object later.
        this.shipImage = new Image();
        this.shipImage.onload = () => { this.drawLayers(); }
        this.shipImage.src = 'data:image/svg+xml;utf8,' + shipShape;

        this.fetchLimits();
    }

    connectedCallback() {
        super.connectedCallback();
        VoyageRoutingModel.addEventListener('weather-limits-changed', this.fetchLimits);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VoyageRoutingModel.removeEventListener('weather-limits-changed', this.fetchLimits);
    }

    fetchLimits() {
        this._weatherLimits = VoyageRoutingModel.getWeatherLimits();

        console.log('limits', this._weatherLimits);
    }

    drawLayers() {
        // Skip this until the ship image has loaded
        if (!this.shipImage.complete) return;

        // Clear the canvas and create an OpenLayers canvas context
        const canvas = this.shadowRoot.querySelector('canvas');
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        const olRender = toContext(context, { size: dialCanvasDimensions });

        // Render the track
        const trackStyle = new Style({
            stroke: new Stroke({
                color: getComputedStyle(this).getPropertyValue('--neutrals-grey-05') || 'grey', width: 1
            })
        });
        olRender.drawFeature(new Feature(new LineString([
            this._pointOnCircle(dialCanvasCenter, dialCanvasRadius, this.heading),
            this._pointOnCircle(dialCanvasCenter, dialCanvasRadius, this.heading - 180)
        ])), trackStyle);

        // Render the ship
        const shipStyle = new Style({
            image: new Icon({
                img: this.shipImage,
                imgSize: shipShapeDimensions,
                rotation: (this.heading-90) * Math.PI / 180
            })
        });
        olRender.drawFeature(new Feature(new Point(dialCanvasCenter)), shipStyle);

        // Render the swell
        const swell = this._computeScaling(this._weatherElements.swell.value, SWELL_SCALING);

        const swellFeature = new Feature({
            geometry: new Point(this._pointOnCircle(dialCanvasCenter, swell[1], this._weatherElements.swell.heading)),
            Value: swell[0],
            Direction: 180-this._weatherElements.swell.heading
        });
        const swellStyle = this.stylesFactory.getStyle({element: 'Swell', view: 'grid'})(swellFeature, swell[2]);
        swellStyle.getStroke().setWidth(2);
        olRender.drawFeature(swellFeature, swellStyle);

        // Render the current
        const current = this._computeScaling(this._weatherElements.current.value, CURRENT_SCALING);

        const currentFeature = new Feature({
            geometry: new Point(this._pointOnCircle(dialCanvasCenter, current[1], this._weatherElements.current.heading)),
            Value: current[0],
            Direction: 180-this._weatherElements.current.heading
        });
        const currentStyle = this.stylesFactory.getStyle({element: 'Currents', view: 'grid'})(currentFeature, current[2]);
        currentStyle.getStroke().setWidth(2);
        olRender.drawFeature(currentFeature, currentStyle);

        // Render the sea (wave)
        const sea = this._computeScaling(this._weatherElements.wave.value, WAVE_SCALING);

        const seaFeature = new Feature({
            geometry: new Point(this._pointOnCircle(dialCanvasCenter, sea[1], this._weatherElements.wave.heading)),
            Value: sea[0],
            Direction: 180-this._weatherElements.wave.heading
        });
        const seaStyle = this.stylesFactory.getStyle({element: 'Wave', view: 'grid'})(seaFeature, sea[2]);
        seaStyle.getStroke().setWidth(2);
        olRender.drawFeature(seaFeature, seaStyle);

        // Render the wind
        const windFeature = new Feature({
            geometry: new Point(this._pointOnCircle(dialCanvasCenter, WIND_SCALING.innerRadius, this._weatherElements.wind.heading)),
            Value: this._weatherElements.wind.value,
            Direction: 180-this._weatherElements.wind.heading
        });
        const windStyle = this.stylesFactory.getStyle({element: 'Wind10M', view: 'grid', additionalOptions: {unit: 'mps'}})(windFeature, 0.8);
        windStyle.getStroke().setWidth(2);
        olRender.drawFeature(windFeature, windStyle);
    }

    // Gets a point on a circle as defined by origin, heading and radius
    _pointOnCircle(origin, radius, heading) {
        const angle = (180 - heading) * Math.PI / 180;
        return [origin[0] + radius * Math.sin(angle), origin[1] + radius * Math.cos(angle)];
    }

    // Computes the required scaling parameters to correctly place a weather indicator arrow
    _computeScaling(value, scalingParameters) {
        const parsedValue = Math.min(value, scalingParameters.maxValue) * scalingParameters.multiplier;

        const resolution = (scalingParameters.outerRadius - scalingParameters.innerRadius) / (3.5 * scalingParameters.styleSizeFactor * Math.log10(scalingParameters.maxValue * scalingParameters.multiplier + 1));

        const size = 3.5 * scalingParameters.styleSizeFactor * resolution * Math.log10(parsedValue + 1);

        return [parsedValue, scalingParameters.outerRadius - size, resolution];
    }
};

customElements.define('voyage-ship-wx', VoyageShipWeatherExpectation);
